"use client";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import { Link } from "@/i18n/navigation";

// Маппинг локали → дефолтный регион
const LOCALE_TO_REGION: Record<string, string> = {
  "en-US": "us",
  "ru-RU": "eu",
  "es-MX": "mx",
  "pt-BR": "br",
  "fr-FR": "eu",
  "ar-AR": "ar",
  "hi-IN": "in",
  "ja-JP": "jp",
  "ko-KR": "kr",
};

const REGIONS: Record<string, { name: string; flag: string; law: string }> = {
  eu:    { name: "European Union",        flag: "🇪🇺", law: "GDPR" },
  us:    { name: "United States",         flag: "🇺🇸", law: "CCPA/CPRA" },
  ca:    { name: "Canada",                flag: "🇨🇦", law: "PIPEDA" },
  br:    { name: "Brasil",                flag: "🇧🇷", law: "LGPD" },
  mx:    { name: "México",                flag: "🇲🇽", law: "LFPDPPP" },
  latam: { name: "Latinoamérica",         flag: "🌎", law: "Various" },
  in:    { name: "India",                 flag: "🇮🇳", law: "DPDP Act 2023" },
  ar:    { name: "Arab Countries",        flag: "🇦🇪", law: "Various" },
  cd:    { name: "China",                 flag: "🇨🇳", law: "PIPL" },
  jp:    { name: "Japan",                 flag: "🇯🇵", law: "APPI" },
  kr:    { name: "South Korea",           flag: "🇰🇷", law: "PIPA" },
  drc:   { name: "DR Congo",              flag: "🇨🇩", law: "Law 22/068" },
};

const LABELS: Record<string, { title: string; detected: string; redirecting: string; manual: string; choose: string }> = {
  "en-US": { title: "Privacy Policy",      detected: "Detected region",  redirecting: "Redirecting...",  manual: "Choose manually", choose: "Select your region:" },
  "ru-RU": { title: "Конфиденциальность",  detected: "Определён регион", redirecting: "Перенаправляем...", manual: "Выбрать вручную", choose: "Выберите регион:" },
  "es-MX": { title: "Privacidad",          detected: "Región detectada", redirecting: "Redirigiendo...", manual: "Elegir manualmente", choose: "Seleccione su región:" },
  "pt-BR": { title: "Privacidade",         detected: "Região detectada", redirecting: "Redirecionando...", manual: "Escolher manualmente", choose: "Selecione sua região:" },
  "fr-FR": { title: "Confidentialité",     detected: "Région détectée",  redirecting: "Redirection...",  manual: "Choisir manuellement", choose: "Sélectionnez votre région:" },
  "ar-AR": { title: "الخصوصية",             detected: "المنطقة المكتشفة", redirecting: "جاري التحويل...", manual: "اختيار يدوي", choose: "اختر منطقتك:" },
  "hi-IN": { title: "गोपनीयता",             detected: "पहचाना गया क्षेत्र", redirecting: "रीडायरेक्ट...", manual: "स्वयं चुनें", choose: "अपना क्षेत्र चुनें:" },
  "ja-JP": { title: "プライバシー",          detected: "検出された地域",     redirecting: "リダイレクト中...", manual: "手動で選択", choose: "地域を選択:" },
  "ko-KR": { title: "개인정보 보호",         detected: "감지된 지역",       redirecting: "리디렉션 중...",   manual: "수동 선택", choose: "지역 선택:" },
};

export default function PrivacyRegionSelector() {
  const locale = useLocale();
  const router = useRouter();
  const detectedRegion = LOCALE_TO_REGION[locale] ?? "us";
  const [showSelector, setShowSelector] = useState(false);
  const labels = LABELS[locale] ?? LABELS["en-US"];

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wantsSelect = params.get("select") === "1";
    const manualOverride = sessionStorage.getItem("legal_privacy_manual") === "1";

    if (wantsSelect || manualOverride) {
      setShowSelector(true);
      return;
    }

    const timer = setTimeout(() => {
      router.replace(`/${locale}/privacy/${detectedRegion}`);
    }, 1500);
    return () => clearTimeout(timer);
  }, [router, detectedRegion, locale]);

  const handleManualChoice = (region: string) => {
    sessionStorage.setItem("legal_privacy_manual", "1");
    router.replace(`/${locale}/privacy/${region}`);
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 py-12">
      <div className="max-w-md w-full">
        <h1 className="text-2xl font-semibold text-slate-900 mb-6 text-center">{labels.title}</h1>

        {!showSelector ? (
          <div className="text-center space-y-4">
            <p className="text-sm text-slate-500">
              {labels.detected}: <span className="text-slate-900 font-medium">
                {REGIONS[detectedRegion]?.flag} {REGIONS[detectedRegion]?.name}
              </span>
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
              <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
              {labels.redirecting}
            </div>
            <button
              onClick={() => setShowSelector(true)}
              className="text-xs text-slate-400 hover:text-slate-700 underline underline-offset-2"
            >
              {labels.manual}
            </button>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-4 text-center">{labels.choose}</p>
            <div className="grid grid-cols-1 gap-2">
              {Object.entries(REGIONS).map(([code, info]) => (
                <button
                  key={code}
                  onClick={() => handleManualChoice(code)}
                  className="flex items-center justify-between px-4 py-3 rounded-xl border border-slate-200 hover:border-slate-400 hover:bg-slate-50 transition-all text-start"
                >
                  <span className="flex items-center gap-2.5">
                    <span className="text-lg">{info.flag}</span>
                    <span className="text-sm font-medium text-slate-900">{info.name}</span>
                  </span>
                  <span className="text-xs text-slate-400 font-mono">{info.law}</span>
                </button>
              ))}
            </div>
          </>
        )}

        <div className="mt-8 text-center">
          <Link href="/" className="text-xs text-slate-400 hover:text-slate-700">‹ Home</Link>
        </div>
      </div>
    </div>
  );
}